import logger from '@/utils/logger'

import { dangerouslyUseRawSQL, SQL, type SQLFragment } from '@/client/query'

import type { AssociationDefinitions } from './associations'
import type { AcceptedAttributes } from './types'

const ALLOWED_NAME_REGEXP = /^[a-z_]+$/
// Only allow a-z and underscores in alias names
// for both attributes and table names

type SQLFragmentAttributes<TModel extends AcceptedAttributes, TKeys extends keyof TModel> = {
  [K in TKeys]: SQLFragment<
    TModel[K] extends Date ? string : TModel[K] extends Date | null ? string | null : TModel[K]
    // ^ Transform Date type into string, which is what we get from
    // node-pg
  >
}
type QueryModel<T extends AcceptedAttributes> = {
  associations: () => AssociationDefinitions
  deleted_at?: SQLFragment<string | null>
} & SQLFragmentAttributes<T, keyof T> & {
    // In query builder, usually properties look like "table_name"."column_name".
    // 'unqualified' contains the "column_name" without the "table_name" qualifier.
    unqualified: SQLFragmentAttributes<T, keyof T>
  } & SQLFragment<T>
// ^ The root object is a fragment itself, like SQL`${User}` is valid

/**
 * @deprecated Use makeQueryModelV2 instead
 */
// Validates and screams loudly if something is out of the ordinary
function makeQueryModel<T extends AcceptedAttributes>(
  alias: string,
  attributes: (keyof T & string)[]
): QueryModel<T> {
  if (!ALLOWED_NAME_REGEXP.test(alias)) {
    logger.error('Error creating alias, invalid characters', {
      alias,
    })
    throw new Error('Internal error')
  }

  const escapedAlias = JSON.stringify(alias)

  const queryModel: Record<string, unknown> = {
    ...dangerouslyUseRawSQL({ __sql: escapedAlias }),
  }
  const unqualified: Record<string, SQLFragment<unknown>> = {}

  attributes.forEach(attribute => {
    if (!ALLOWED_NAME_REGEXP.test(attribute)) {
      logger.error('Error creating alias attributes, invalid characters', {
        alias,
        attribute,
        attributes,
      })
      throw new Error('Internal error')
    }

    const unqualifiedAttribute = JSON.stringify(attribute)

    queryModel[attribute] = dangerouslyUseRawSQL({
      __sql: `${escapedAlias}.${unqualifiedAttribute}`,
    })

    unqualified[attribute] = dangerouslyUseRawSQL({
      __sql: unqualifiedAttribute,
    })
  })

  queryModel.unqualified = unqualified

  return queryModel as QueryModel<T>
}

// v2 filters the attributes to only the ones specified in code
// A bunch of different methods were explored to automatically infer the attributes type
// from the arguments, but Typescript didn't cooperate. In the end, ended up going with
// a "currying" method, to make it multiple function calls, with stacking types.
export type QueryModelV2<
  T extends AcceptedAttributes,
  T2 extends keyof T & string = keyof T & string,
> = {
  associations: () => AssociationDefinitions
  deleted_at?: SQLFragment<string | null>
} & SQLFragmentAttributes<T, T2> & {
    // In query builder, usually properties look like "table_name"."column_name".
    // 'unqualified' contains the "column_name" without the "table_name" qualifier.
    unqualified: SQLFragmentAttributes<T, T2>
  } & SQLFragment<T>
// ^ The root object is a fragment itself, like SQL`${User}` is valid

export function makeQueryModelV2<T extends AcceptedAttributes>(
  alias: string
): <T2 extends keyof T & string>(attributes: T2[]) => QueryModelV2<T, T2> {
  return <T2 extends keyof T & string>(attributes: T2[]) =>
    makeQueryModel<T>(alias, attributes) as QueryModelV2<T, T2>
}

export type QueryOrderDirection = 'asc' | 'desc'
export type QueryOrder = [SQLFragment<unknown>, QueryOrderDirection][]

export function queryOrderToSQL(order: QueryOrder | null): SQLFragment<never> {
  if (order == null || order.length === 0) {
    return SQL``
  }

  return order.reduce(
    (agg, curr, i) =>
      SQL`${agg}${i === 0 ? SQL`` : SQL`,`} ${curr[0]} ${curr[1] === 'asc' ? SQL`ASC` : SQL`DESC`}`,
    SQL`ORDER BY`
  )
}

export type GroupBy = SQLFragment<unknown>[]

export function queryGroupByToSQL(groupBy: GroupBy | null): SQLFragment<never> {
  if (groupBy == null || groupBy.length === 0) {
    return SQL``
  }

  return groupBy.reduce(
    (agg, curr, i) => SQL`${agg}${i === 0 ? SQL`` : SQL`,`} ${curr}`,
    SQL`GROUP BY`
  )
}

export function queryLimitToSQL(limit: number | null): SQLFragment<never> {
  return limit != null ? SQL`LIMIT ${limit}` : SQL``
}

export function queryOffsetToSQL(offset: number | null): SQLFragment<never> {
  return offset != null ? SQL`OFFSET ${offset}` : SQL``
}

export function getFieldIf<T>(
  field: SQLFragment<T>,
  customWhere: SQLFragment<boolean>
): SQLFragment<T | null> {
  return SQL`CASE WHEN (${customWhere}) THEN ${field} ELSE NULL END`
}

export * from './associations'
